/**
 * Table row for a field event.
 */
import * as React from 'react';
import { Calendar, Clock, Building2, UserCheck, Repeat2, FileEdit, BookOpen } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableRow } from '@/components/common/data-table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { EVENT_STATUS_CONFIG, EVENT_RECURRENCE_LABELS } from './event-configs';
import { getRowPermission } from '@/hooks/usePageActions';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  type EventItem,
  formatEventTime,
  formatEventDate,
  deriveEventActivities,
  getEventActionState,
  useNavigateToEventSummary,
} from './event-table-utils';

/** Props for {@link EventRow}. */
interface EventRowProps {
  event: EventItem;
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onCancel?: (id: string) => void;
  onManageAttendance?: (id: string) => void;
  pageActions?: {
    canEditRow?: boolean;
    canCancelRow?: boolean;
    canManageAttendance?: boolean;
    canDeleteRow?: boolean;
    canViewSummary?: boolean;
    canEditSummary?: boolean;
  };
}

/** Table row for a field event. */
export const EventRow: React.FC<EventRowProps> = ({
  event,
  onView,
  onEdit,
  onCancel,
  onManageAttendance,
  pageActions,
}) => {
  const statusConfig = EVENT_STATUS_CONFIG[event.status];
  const { navigateToSummary } = useNavigateToEventSummary();

  const canEdit = getRowPermission(event, 'action-edit-row', pageActions?.canEditRow ?? true);
  const canCancel = getRowPermission(event, 'action-cancel-row', pageActions?.canCancelRow ?? true);
  const canAttendance = getRowPermission(
    event,
    'action-manage-attendance',
    pageActions?.canManageAttendance ?? true,
  );
  const canEditSummary = pageActions?.canEditSummary ?? false;

  const { isEditable, isCancelable, isAttendanceEnabled } = getEventActionState(
    event,
    canEdit,
    canCancel,
    canAttendance,
  );

  const dateLabel = formatEventDate(event.eventDate, 'EEEE d MMM yyyy');

  const { firstActivity, extraActivities, firstActivityTypeLabel } = deriveEventActivities(event);

  return (
    <TableRow onClick={() => onView?.(event.id)}>
      <td className="min-w-[200px] p-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)] shrink-0">
              <Calendar className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
            </div>
            <span className="font-medium text-foreground capitalize">{dateLabel}</span>
          </div>
          <div className="flex items-center gap-2 ml-10">
            <Clock className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)]" />
            <span className="text-sm text-muted-foreground">
              {formatEventTime(event.startTime)} - {formatEventTime(event.endTime)}
            </span>
          </div>
          {event.mode === 'recurring' &&
            event.recurrenceType &&
            event.recurrenceType !== 'none' && (
              <div className="ml-10">
                <LabelBadge
                  label={EVENT_RECURRENCE_LABELS[event.recurrenceType] || 'Récurrente'}
                  icon={Repeat2}
                  variant="outlined"
                  color="var(--color-4-nuance-1)"
                  size="sm"
                />
              </div>
            )}
        </div>
      </td>

      <td className="min-w-[150px] p-3">
        {(event.establishmentName || event.establishment) && (
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4" style={{ color: 'var(--color-1-nuance-1)' }} />
            <span className="text-foreground">
              {event.establishmentName || event.establishment?.name}
            </span>
          </div>
        )}
      </td>

      <td className="min-w-[160px] p-3">
        {firstActivity ? (
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-1.5 flex-wrap">
              <BookOpen className="w-4 h-4 shrink-0" style={{ color: 'var(--color-1-nuance-1)' }} />
              <span className="font-medium text-foreground">{firstActivity.label}</span>
              {extraActivities.length > 0 && (
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold cursor-default select-none bg-[color-mix(in_srgb,var(--color-4-nuance-1)_12%,transparent)] text-[var(--color-4-nuance-1)] border border-[color-mix(in_srgb,var(--color-4-nuance-1)_30%,transparent)]">
                        +{extraActivities.length}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent variant="light" side="top" className="max-w-xs">
                      <ul className="space-y-1 text-xs">
                        {extraActivities.map((a) => {
                          const t = a.activityTypeName ?? null;
                          return (
                            <li key={a.id ?? `${a.label}-${t ?? ''}`} className="flex flex-col">
                              <span className="font-medium">{a.label}</span>
                              {t && <span className="text-muted-foreground">Type : {t}</span>}
                            </li>
                          );
                        })}
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            {firstActivityTypeLabel && (
              <span className="text-xs text-muted-foreground pl-6">
                Type&nbsp;: {firstActivityTypeLabel}
              </span>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </td>

      <td className="min-w-[120px] p-3">
        <LabelBadge
          label={statusConfig.label}
          icon={
            statusConfig.isInProgress ? <span className="session-dot-pulse" /> : statusConfig.icon
          }
          variant={statusConfig.badgeVariant}
          color={statusConfig.badgeColor}
        />
      </td>

      <td className="min-w-[200px] p-3">
        <div className="flex items-center gap-1 justify-end">
          <Button preset="row-view" onClick={() => onView?.(event.id)} />

          {onEdit && (
            <Button
              preset="row-edit"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(event.id);
              }}
              disabled={!isEditable}
              tooltipText={isEditable ? 'Modifier' : getErrorMessage('cannotEdit')}
            />
          )}

          {onManageAttendance && (
            <Button
              variant="ghost"
              color="#7c3aed"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                onManageAttendance(event.id);
              }}
              icon={UserCheck}
              disabled={!isAttendanceEnabled}
              tooltipText={
                isAttendanceEnabled
                  ? 'Gérer les présences'
                  : getErrorMessage('eventAttendanceUnavailable')
              }
            />
          )}

          {canEditSummary && (
            <Button
              variant="ghost"
              color="var(--color-3-nuance-1)"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                void navigateToSummary(event.id, 'edit');
              }}
              icon={FileEdit}
              tooltipText="Modifier la synthèse"
            />
          )}

          {onCancel && (
            <Button
              preset="row-cancel"
              onClick={(e) => {
                e.stopPropagation();
                onCancel(event.id);
              }}
              disabled={!isCancelable}
              tooltipText={isCancelable ? "Annuler l'événement" : getErrorMessage('cannotCancel')}
            />
          )}
        </div>
      </td>
    </TableRow>
  );
};
